import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserLogin() {
  const initialFormState = {
    name: '', email: '', password: '', confirmPassword: '', otp: ''
  };
  const [isLogin, setIsLogin] = useState(true);
  const [step, setStep] = useState(1); // 1=Form, 2=OTP
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState(initialFormState);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const loadGoogleScript = () =>
    new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) return resolve();
      const existing = document.getElementById('google-identity-script');
      if (existing) {
        existing.onload = resolve;
        existing.onerror = reject;
        return;
      }
      const script = document.createElement('script');
      script.id = 'google-identity-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });

  const handleGoogleAuth = async () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast.error('Google Sign-In is not configured. Add REACT_APP_GOOGLE_CLIENT_ID in your frontend environment and restart the app.');
      return;
    }
    setGoogleLoading(true);
    try {
      await loadGoogleScript();
      const credential = await new Promise((resolve, reject) => {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => resolve(response.credential),
        });
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            reject(new Error('Google prompt unavailable'));
          }
        });
      });
      await googleLogin(credential);
      toast.success('Signed in with Google successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google Sign-In failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // === LOGIN ===
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Uses existing login logic (works for both users and admins)
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/'); // Redirect to Home Page, NOT Admin Dashboard
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // === SIGNUP STEP 1: SEND OTP ===
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/student/signup-otp`, {
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      toast.success(`OTP sent to ${formData.email}`);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // === SIGNUP STEP 2: VERIFY ===
  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/student/verify`, {
        email: formData.email,
        otp: formData.otp,
        name: formData.name,
        password: formData.password
      });
      toast.success('Account created! Please login.');
      setIsLogin(true); // Switch to Login View
      setStep(1);
      setFormData(initialFormState);
    } catch (error) {
      toast.error('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-4 transition-colors">

      <Card className="w-full max-w-md shadow-2xl border-border bg-card/95 backdrop-blur">
        <CardHeader>
          <Button variant="ghost" className="w-fit pl-0 mb-2 text-muted-foreground hover:text-foreground" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Home
          </Button>
          <CardTitle className="text-2xl font-bold text-center">
            {isLogin ? 'User Login' : step === 1 ? 'Create Account' : 'Verify Email'}
          </CardTitle>
          <CardDescription className="text-center text-muted-foreground">
            {isLogin ? 'Access your K-Crunch account' : 'Create your account with your email'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 rounded-lg border border-border bg-muted/40 p-3">
            <Button type="button" onClick={handleGoogleAuth} disabled={googleLoading} className="w-full bg-white hover:bg-gray-100 text-black font-semibold">
              {googleLoading ? (
                <Loader2 className="animate-spin mr-2" />
              ) : (
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  className="w-4 h-4 mr-2"
                />
              )}
              Continue with Google
            </Button>
          </div>
          <div className="relative text-center text-xs text-muted-foreground mb-4">
            <span className="bg-card px-2 relative z-10">or continue with email</span>
            <div className="absolute left-0 right-0 top-1/2 h-px bg-border -z-0" />
          </div>
          {isLogin ? (
            // LOGIN FORM
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input data-testid="login-email" name="email" type="email" placeholder="example@gmail.com" value={formData.email} onChange={handleChange} autoComplete="email" required />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input data-testid="login-password" name="password" type="password" value={formData.password} onChange={handleChange} autoComplete="current-password" required />
              </div>
              <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold transition-all hover:scale-[1.01]" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" /> : 'Login'}
              </Button>
              <p className="text-center text-sm mt-4 text-muted-foreground">
                First time here? <span onClick={() => { setIsLogin(false); setStep(1); setFormData(initialFormState); }} className="text-amber-400 font-bold cursor-pointer hover:underline">Sign Up</span>
              </p>
            </form>
          ) : step === 1 ? (
            // SIGNUP FORM
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input data-testid="signup-name" name="name" placeholder="Ananya Panday" value={formData.name} onChange={handleChange} autoComplete="name" required />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input data-testid="signup-email" name="email" type="email" placeholder="example@domain.com" value={formData.email} onChange={handleChange} autoComplete="email" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input name="password" type="password" value={formData.password} onChange={handleChange} autoComplete="new-password" required />
                </div>
                <div className="space-y-2">
                  <Label>Confirm</Label>
                  <Input name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} autoComplete="new-password" required />
                </div>
              </div>
              <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold transition-all hover:scale-[1.01]" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" /> : 'Send OTP'}
              </Button>
              <p className="text-center text-sm mt-4 text-muted-foreground">
                Already have an account? <span onClick={() => { setIsLogin(true); setStep(1); setFormData(initialFormState); }} className="text-amber-400 font-bold cursor-pointer hover:underline">Login</span>
              </p>
            </form>
          ) : (
            // OTP FORM
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Label>Enter Verification Code</Label>
                <Input data-testid="signup-otp" name="otp" className="text-center text-2xl tracking-widest" maxLength={6} value={formData.otp} onChange={handleChange} autoComplete="one-time-code" required />
                <p className="text-xs text-muted-foreground text-center">Sent to {formData.email}</p>
              </div>
              <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" /> : 'Verify & Register'}
              </Button>
              <Button type="button" variant="ghost" className="w-full text-sm" onClick={() => setStep(1)}>
                Change Email
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
